import { fabric } from 'fabric';
import { cloneFabricObject } from "@frontend/lib/fabric/effects/helpers/clone-handler";

export class OuterGlowEffect {
  isActive(obj) {
    if (!obj) {
      return false;
    }

    return obj.canvas && obj.element_id && obj.outerGlow;
  }

  apply(obj, effectsRenderBuffer) {
    let { size, spread, color, cache = {} } = obj.outerGlow;
    let { alpha = [] }  = cache;
    let offsetX = effectsRenderBuffer.offsetX;
    let offsetY = effectsRenderBuffer.offsetY;
    let canvasWidth = effectsRenderBuffer.canvas ? effectsRenderBuffer.canvas.width : obj.getScaledWidth();
    let canvasHeight = effectsRenderBuffer.canvas ? effectsRenderBuffer.canvas.height : obj.getScaledHeight();

    canvasWidth += size;
    canvasHeight += size;
    offsetX += size / 2;
    offsetY += size / 2;

    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width;
    maskCanvas.height = canvas.height;
    const maskCtx = maskCanvas.getContext('2d');

    const clone = effectsRenderBuffer.canvas
      ? new fabric.Image(effectsRenderBuffer.canvas)
      : cloneFabricObject(obj, {
        left: size / 2,
        top: size / 2,
        outerGlow: null,
      });

    if (obj.clipPath && !effectsRenderBuffer.canvas) {
      const cp = fabric.util.object.clone(obj.clipPath);
      cp.absolutePositioned = true;

      const objRect = obj.getBoundingRect(true);
      const cloneRect = clone.getBoundingRect(true);

      const clipOffsetX = cloneRect.left - objRect.left;
      const clipOffsetY = cloneRect.top - objRect.top;

      cp.left = (cp.left || 0) + clipOffsetX;
      cp.top = (cp.top || 0) + clipOffsetY;

      clone.clipPath = cp;
    }
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvasWidth;
    tempCanvas.height = canvasHeight;
    const tempCtx = tempCanvas.getContext('2d');
    clone.render(tempCtx);

    if (!alpha.length) {
      alpha = this._prepareAlpha(maskCanvas, maskCtx, tempCanvas, size);
    }

    const finalAlpha = this._applySpread(alpha, spread);
    this._renderOuterGlow(ctx, canvasWidth, canvasHeight, finalAlpha, color);

    if (effectsRenderBuffer.canvas) {
      ctx.drawImage(effectsRenderBuffer.canvas, offsetX, offsetY);
    } else {
      clone.render(ctx);
    }

    return {
      canvas: canvas,
      offsetX: offsetX,
      offsetY: offsetY
    }
  }

  _applySpread(alpha, spread) {
    const out = new Float32Array(alpha.length);

    for (let i = 0; i < alpha.length; i++) {
      let v = alpha[i];
      out[i] = v * spread / 10;
    }
    return out;
  }

  _blurAlpha(alpha, w, h, radius) {
    if (radius <= 0) return alpha;
    const kernel = this._createGaussianKernel(radius);
    const tmp = new Float32Array(alpha.length);

    // Horizontal
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let val = 0;
        for (let k = -radius; k <= radius; k++) {
          const ix = Math.min(w-1, Math.max(0, x+k));
          val += alpha[y*w + ix] * kernel[k+radius];
        }
        tmp[y*w + x] = val;
      }
    }

    // Vertical
    for (let x = 0; x < w; x++) {
      for (let y = 0; y < h; y++) {
        let val = 0;
        for (let k = -radius; k <= radius; k++) {
          const iy = Math.min(h-1, Math.max(0, y+k));
          val += tmp[iy*w + x] * kernel[k+radius];
        }
        alpha[y*w + x] = val;
      }
    }
    return alpha;
  }

  _createGaussianKernel(radius) {
    const sigma = radius / 3;
    const size = radius * 2 + 1;
    const kernel = new Float32Array(size);
    let sum = 0;
    for (let i = 0; i < size; i++) {
      const x = i - radius;
      kernel[i] = Math.exp(-(x*x)/(2*sigma*sigma));
      sum += kernel[i];
    }
    for (let i = 0; i < size; i++) kernel[i] /= sum;
    return kernel;
  }

  _renderOuterGlow(ctx, w, h, finalAlpha, color) {
    const glowData = ctx.createImageData(w, h);

    let r, g, b, baseA = 1;

    if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else if (hex.length === 8) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
        baseA = parseInt(hex.substring(6, 8), 16) / 255;
      }
    } else if (color.startsWith("rgba")) {
      const parts = color.match(/rgba?\(([^)]+)\)/)[1].split(",").map(p => parseFloat(p.trim()));
      [r, g, b] = parts;
      if (parts.length === 4) baseA = parts[3];
    } else if (color.startsWith("rgb")) {
      const parts = color.match(/rgb\(([^)]+)\)/)[1].split(",").map(p => parseInt(p.trim(), 10));
      [r, g, b] = parts;
    }

    for (let i = 0; i < finalAlpha.length; i++) {
      glowData.data[i * 4]     = r;
      glowData.data[i * 4 + 1] = g;
      glowData.data[i * 4 + 2] = b;
      glowData.data[i * 4 + 3] = Math.min(255, finalAlpha[i] * baseA * 255);
    }

    ctx.putImageData(glowData, 0, 0);
  }


  _prepareAlpha(maskCanvas, maskCtx, imageCanvas, size) {
    const w = maskCanvas.width;
    const h = maskCanvas.height;

    maskCtx.clearRect(0, 0, w, h);
    maskCtx.drawImage(imageCanvas, 0, 0, w, h);

    const imgData = maskCtx.getImageData(0,0,w,h);
    const alpha = new Float32Array(w*h);
    for (let i=0;i<w*h;i++) alpha[i] = imgData.data[i*4+3]/255;

    this._blurAlpha(alpha, w, h, size);

    return alpha;
  }
}
